import * as z from 'zod';

// A promotion (Скидки и акции) — a discounted product on its own table page,
// plus the taxonomies that feed the page's filter selects.

/**
 * The row's moderation verdict, normalized from the API's
 * `MERCHANT_MODERATION_STATE_*` (same vocabulary as the branch badges).
 * Absent when the read carries no verdict (bare `by-restaurant` rows, or
 * `UNSPECIFIED`) — the row then falls back to the legacy `isActive` label.
 */
export const PROMOTION_MODERATIONS = [
  'draft',
  'pending',
  'approved',
  'rejected',
] as const;
export const promotionModerationSchema = z.enum(PROMOTION_MODERATIONS);
export type PromotionModeration = z.infer<typeof promotionModerationSchema>;

export const promotionSchema = z.object({
  /**
   * The PROMOTION's id — shared by every row of a batch campaign, so it is NOT
   * unique across the table. It addresses the whole campaign, so it is never
   * enough on its own to act on ONE row: use {@link promotionRowKey} to tell a
   * row apart from its siblings, and pair the id with `productId` /
   * `restaurantId` to delete just that pair.
   */
  id: z.union([z.string(), z.number()]),
  /**
   * The product and branch this row joins the promotion to.
   *
   * A promotion covers a cross product of products × branches and the list
   * endpoint returns one JOINED row per pair, all sharing `id` — so without
   * these a row cannot be told apart from its siblings, and React saw
   * duplicate keys (which is what left the table misaligned after a delete
   * until a reload).
   *
   * They also name the pair a delete acts on — both the table's per-row delete
   * and the editor unchecking a product go through
   * `DELETE /promotions/{id}/products`, which is per-branch.
   */
  productId: z.string().default(''),
  restaurantId: z.string(),
  productName: z.string().default(''),
  /**
   * The CAMPAIGN's own name, locale-resolved — not the product's.
   *
   * The joined row nests the whole promotion, so this was always on the wire;
   * it simply was not mapped, and the edit form fell back to `productName` as a
   * stand-in. That showed the merchant the wrong thing: a campaign called
   * «Навруз» covering «Медовик» opened with "Медовик" in the name field, and
   * saving without touching it renamed the campaign after one of its products.
   *
   * Defaults to `''` for a legacy row whose promotion carries no name — the
   * form then simply starts blank rather than seeding a product name.
   */
  name: z.string().default(''),
  imageUrl: z.string().optional(),
  /** Original price in so'm (integer — so'm has no minor units). */
  price: z.number().nonnegative().default(0),
  discountPercent: z.number().int().min(0).max(100),
  /**
   * How the discount was entered: a percentage or a fixed final price. Stored so
   * the edit form can round-trip the merchant's original intent. Defaults to
   * `'percent'` for legacy rows.
   */
  discountType: z.enum(['percent', 'fixed']).default('percent'),
  /** Price after the discount, in so'm. */
  finalPrice: z.number().nonnegative().default(0),
  branchName: z.string().default(''),
  discountValue: z.number().nonnegative().optional(),
  isActive: z.boolean().optional(),
  moderationState: promotionModerationSchema.optional(),
  /** Why moderation sent the promotion back (`rejection_reason`). */
  rejectionReason: z.string().optional(),
  startsAt: z.string().optional(),
  endsAt: z.string().optional(),
  productIds: z.array(z.string()).optional(),
  /**
   * Promo window: `null` = open-ended (rendered as "бессрочно"); otherwise a
   * preformatted date range from the backend.
   */
  duration: z.string().nullable().default(null),
});
export type Promotion = z.infer<typeof promotionSchema>;

/**
 * A stable, UNIQUE identity for one table row.
 *
 * `id` alone is the campaign, shared across every product × branch pair it
 * covers — so it is a duplicate React key the moment a promotion spans more
 * than one product, and duplicate keys are what made the list render
 * misaligned after a row was removed.
 *
 * Also what the delete mutation matches on when it drops a row from the cache
 * optimistically: filtering by `id` alone would blank every sibling row the
 * request never touched.
 */
export function promotionRowKey(promotion: Promotion): string {
  return promotion.productId
    ? `${String(promotion.id)}:${promotion.restaurantId}:${promotion.productId}`
    : `${String(promotion.id)}:${promotion.restaurantId}`;
}

/**
 * One promotion's own record, as read by id for the edit form.
 *
 * Distinct from {@link promotionSchema}, which is a JOINED
 * `product × restaurant × promotion` row built for the TABLE: that row names
 * its product and branch by display name (all the join gives it), so seeding a
 * form from it meant matching those names back to ids by string — wrong the
 * moment two products share a name. This shape carries the ids outright.
 */
export const promotionDetailSchema = z.object({
  id: z.string(),
  /**
   * The campaign's own name, every translation at once — what seeds the
   * wizard's step 1. The read RPC answers with a `v1LocalizedText`, so this is
   * the one place all three languages are available; the table row carries
   * only the locale-resolved string.
   */
  nameTranslations: z.object({
    ru: z.string(),
    uz: z.string(),
    en: z.string(),
  }),
  /** The branch this record was read for — the read is branch-scoped. */
  restaurantId: z.string(),
  /**
   * The products the promotion covers AT `restaurantId`, not campaign-wide.
   * The endpoint scopes them to the branch the read named, which is what makes
   * the wizard's per-branch checkboxes tickable.
   */
  productIds: z.array(z.string()),
  discountType: z.enum(['percent', 'fixed']),
  /** A percent rate, or the fixed final price in so'm — per `discountType`. */
  discountValue: z.number().nonnegative(),
  /** ISO bounds; absent means that end is open. */
  startsAt: z.string().optional(),
  endsAt: z.string().optional(),
});
export type PromotionDetail = z.infer<typeof promotionDetailSchema>;

export const promotionFilterOptionSchema = z.object({
  id: z.union([z.string(), z.number()]),
  name: z.string(),
});
export type PromotionFilterOption = z.infer<typeof promotionFilterOptionSchema>;

export const promotionsSchema = z.object({
  items: z.array(promotionSchema),
  /** Category options for the filter select. */
  categories: z.array(promotionFilterOptionSchema),
  /** Tag options for the filter select. */
  tags: z.array(promotionFilterOptionSchema),
  /** Preformatted default date-range label shown in the header pill. */
  dateRangeLabel: z.string(),
});
export type Promotions = z.infer<typeof promotionsSchema>;
